import React, { useState, useContext, useEffect } from 'react';
import { AppContext } from 'Context';
import useSWR from 'swr';
import { dataFetcher, endpoints } from 'Api';
import cn from 'classnames';
import styles from '../index.module.css';

const SubCategoriesMenu = ({ defaultValue, isSubcategory, onSelect, placeholder, title }) => {
  const { setAppSnackbar, langText } = useContext(AppContext);
  const categoryText = langText?.content;
  const commonText = langText?.common;
  const [isOpen, setOpen] = useState(false);
  const [val, setVal] = useState(undefined);
  const [options, setOptions] = useState([]);
  const [newCategoryName, setNewCategoryName] = useState([]);
  const [createMenuIsVisible, setCreateMenuVisibility] = useState(false);

  const showCreateMenu = () => { setCreateMenuVisibility(true); };
  const hideCreateMenu = () => { setCreateMenuVisibility(false); };

  const { isValidating: gettingSubCategories, mutate: getSubCategories } = useSWR([endpoints.getMySubCategory], {
    fetcher: (url) => dataFetcher(url),
    onSuccess: ({ success, data }) => {
      if (success) {
        if (defaultValue) {
          data?.every(({ id }, idx) => {
            if (id === defaultValue) {
              setVal(data[idx]?.title);

              return false;
            }

            return true;
          });
        }
        setOptions(data?.map(({ id, title: name }) => ({ name, value: id })));
      } else {
        setAppSnackbar({
          isVisible: true,
          type: 'error',
          message: 'Oops! Something went wrong while fetching your categories',
        });
      }
    },
    onError: () => {
      setAppSnackbar({ isVisible: true, type: 'error', message: 'Oops! Something went wrong while fetching your categories' });
    },
  });

  const { isValidating: addingNewSubCategory, mutate: addNewSubCategory } = useSWR([
    endpoints.addUpdateMySubCategory, newCategoryName
  ], {
    fetcher: (url, categoryName, parentCategoryID) => dataFetcher(url, {
      title: categoryName,
      ...(parentCategoryID && { parent_id: parentCategoryID })
    }),
    onSuccess: ({ success, message }) => {
      if (success) {
        hideCreateMenu();
        getSubCategories();
        setAppSnackbar({
          isVisible: true,
          message: message || `${isSubcategory ? 'Sub-Category' : 'Category'} '${newCategoryName}' was created`,
        });
      } else {
        setAppSnackbar({ isVisible: true, type: 'error', message: 'Oops! Something went wrong while creating your category' });
      }
    },
    onError: () => {
      setAppSnackbar({ isVisible: true, type: 'error', message: 'Oops! Something went wrong while creating your category' });
    },
  });

  useEffect(() => {
    setVal(placeholder);
    getSubCategories();
  }, []);

  return (
    <div className={styles.categoriesMenu}>
      <div className={styles.categoriesMenuTitle}>{title}</div>
      <div
        className={styles.categoriesMenuInput}
        onClick={() => { setOpen(true); }}
        onKeyPress={undefined}
        role="button"
        tabIndex={0}
      >
        <div className={styles.categoriesMenuInputText}>
          {gettingSubCategories ? `${commonText?.loading || 'Loading'}...` : val || placeholder}
        </div>
        <div className={styles.categoriesMenuInputIcon}>▾</div>
      </div>
      {!gettingSubCategories && isOpen && (
        <>
          <div
            className={styles.prefDropdownScrim}
            onClick={() => { if (!addingNewSubCategory) { setCreateMenuVisibility(false); setOpen(false); } }}
            onKeyPress={undefined}
            role="button"
            tabIndex={0}
          />
          <div className={styles.categoriesMenuOptionsContainer}>
            {createMenuIsVisible
              ? (
                <div className={styles.newCategoryContainer}>
                  <div className={styles.newCategoryBar}>
                    {isSubcategory ? (categoryText?.subCategory || 'Enter new Sub-Category') : (categoryText?.category || 'Enter new Category')}
                    <button
                      className={styles.newCategoryButton}
                      disabled={addingNewSubCategory}
                      onClick={hideCreateMenu}
                    >
                      Back
                    </button>
                  </div>
                  <input
                    className={styles.newCategoryInput}
                    onChange={(e) => { setNewCategoryName(e?.target?.value); }}
                    type="text"
                  />
                  <button
                    className={cn({ [styles.newCategoryButton]: true, [styles.newCategoryButtonCreate]: true })}
                    disabled={addingNewSubCategory || !(newCategoryName.toString()?.trim())}
                    onClick={addNewSubCategory}
                  >
                    {addingNewSubCategory ? `${commonText?.creating || 'Creating'}...` : (commonText?.create || 'Create')}
                  </button>
                </div>
              )
              : (
                <>
                  {options.map(({ name, value }, idx) => (
                    <div
                      className={styles.categoriesMenuOption}
                      key={idx}
                      onClick={() => { setVal(name); onSelect(value); setOpen(false); }}
                      onKeyPress={undefined}
                      role="button"
                      tabIndex={0}
                    >
                      {name}
                    </div>
                  ))}
                  <button
                    className={cn({ [styles.categoriesMenuOption]: true, [styles.categoriesMenuOptionAdd]: true })}
                    onClick={showCreateMenu}
                  >
                    {`Add new ${title}`}
                  </button>
                </>
              )}
          </div>
        </>
      )}
    </div>
  );
};
export default SubCategoriesMenu;
